import {
  CACHE_STORE,
  CHECKPOINT_STORE,
  KEY_STORE,
  QUEUE_STORE,
  createQueueRecord,
  emitOfflineChange,
  nowIso,
  type OfflineCacheRecord,
  type OfflineQueueRecord,
  type OfflineStoreAdapter,
} from './offline-store-types';

const DB_NAME = 'naviobuild-offline';
const DB_VERSION = 2;
const ENCRYPTION_KEY_ID = 'offline-payload-key-v1';

class NavioSystemError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NavioSystemError';
  }
}

interface EncryptionEnvelope {
  __naviobuildEncrypted: true;
  alg: 'AES-GCM';
  v: 1;
  iv: string;
  ciphertext: string;
}

type EncryptedCacheRecord<T = unknown> = Omit<OfflineCacheRecord<T>, 'data'> & {
  data: T | EncryptionEnvelope;
};

type EncryptedQueueRecord = Omit<OfflineQueueRecord, 'payload'> & {
  payload: OfflineQueueRecord['payload'] | EncryptionEnvelope;
};

let dbPromise: Promise<IDBDatabase> | null = null;
let encryptionKeyPromise: Promise<CryptoKey | null> | null = null;

export function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

export function resetIndexedDbOfflineStoreForTests(): void {
  dbPromise = null;
  encryptionKeyPromise = null;
}

function hasWebCrypto(): boolean {
  return typeof crypto !== 'undefined' && Boolean(crypto.subtle);
}

function isEncryptionEnvelope(value: unknown): value is EncryptionEnvelope {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    (value as { __naviobuildEncrypted?: unknown }).__naviobuildEncrypted === true
  );
}

function base64FromBytes(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function bytesFromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function arrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function openOfflineDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        const cache = db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
        cache.createIndex('projectId', 'projectId', { unique: false });
        cache.createIndex('routeId', 'routeId', { unique: false });
        cache.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const queue = db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
        queue.createIndex('projectId', 'projectId', { unique: false });
        queue.createIndex('status', 'status', { unique: false });
        queue.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(CHECKPOINT_STORE)) {
        db.createObjectStore(CHECKPOINT_STORE, { keyPath: 'projectId' });
      }
      if (!db.objectStoreNames.contains(KEY_STORE)) {
        db.createObjectStore(KEY_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open offline database'));
  });

  return dbPromise;
}

async function getOfflineEncryptionKey(db: IDBDatabase): Promise<CryptoKey | null> {
  if (!hasWebCrypto()) return null;
  if (encryptionKeyPromise) return encryptionKeyPromise;

  encryptionKeyPromise = (async () => {
    const readTx = db.transaction(KEY_STORE, 'readonly');
    const existing = await requestToPromise<{ id: string; key: CryptoKey } | undefined>(
      readTx.objectStore(KEY_STORE).get(ENCRYPTION_KEY_ID)
    );
    if (existing?.key) return existing.key;

    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    const writeTx = db.transaction(KEY_STORE, 'readwrite');
    writeTx.objectStore(KEY_STORE).put({ id: ENCRYPTION_KEY_ID, key });
    await transactionComplete(writeTx);
    return key;
  })();

  return encryptionKeyPromise;
}

async function encryptValue<T>(db: IDBDatabase, value: T): Promise<T | EncryptionEnvelope> {
  const key = await getOfflineEncryptionKey(db);
  if (!key) return value;

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: arrayBufferFromBytes(iv) },
    key,
    plaintext
  );
  return {
    __naviobuildEncrypted: true,
    alg: 'AES-GCM',
    v: 1,
    iv: base64FromBytes(iv),
    ciphertext: base64FromBytes(new Uint8Array(ciphertext)),
  } satisfies EncryptionEnvelope;
}

async function decryptValue<T>(db: IDBDatabase, value: T | EncryptionEnvelope): Promise<T> {
  if (!isEncryptionEnvelope(value)) return value;

  const key = await getOfflineEncryptionKey(db);
  if (!key) throw new NavioSystemError('Offline encryption key is unavailable');

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: arrayBufferFromBytes(bytesFromBase64(value.iv)) },
    key,
    arrayBufferFromBytes(bytesFromBase64(value.ciphertext))
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

async function decryptCacheRecord<T>(
  db: IDBDatabase,
  record: EncryptedCacheRecord<T> | undefined
): Promise<OfflineCacheRecord<T> | null> {
  if (!record) return null;
  return { ...record, data: await decryptValue<T>(db, record.data) };
}

async function decryptQueueRecord(db: IDBDatabase, record: EncryptedQueueRecord): Promise<OfflineQueueRecord> {
  return { ...record, payload: await decryptValue<OfflineQueueRecord['payload']>(db, record.payload) };
}

export const indexedDbAdapter: OfflineStoreAdapter = {
  async getCache<T>(key: string) {
    const db = await openOfflineDb();
    const tx = db.transaction(CACHE_STORE, 'readonly');
    const record = await requestToPromise<EncryptedCacheRecord<T> | undefined>(
      tx.objectStore(CACHE_STORE).get(key)
    );
    return decryptCacheRecord<T>(db, record);
  },

  async setCache<T>(record: OfflineCacheRecord<T>) {
    const db = await openOfflineDb();
    const storedRecord = { ...record, data: await encryptValue(db, record.data) } satisfies EncryptedCacheRecord<T>;
    const tx = db.transaction(CACHE_STORE, 'readwrite');
    tx.objectStore(CACHE_STORE).put(storedRecord);
    await transactionComplete(tx);
    emitOfflineChange();
  },

  async listQueuedOperations(statuses) {
    const db = await openOfflineDb();
    const tx = db.transaction(QUEUE_STORE, 'readonly');
    const records = await requestToPromise<EncryptedQueueRecord[]>(tx.objectStore(QUEUE_STORE).getAll());
    const decryptedRecords = await Promise.all(records.map((record) => decryptQueueRecord(db, record)));
    return decryptedRecords
      .filter((record) => !statuses || statuses.includes(record.status))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  },

  async queueOperation(input) {
    const record = createQueueRecord(input);
    const db = await openOfflineDb();
    const storedRecord = { ...record, payload: await encryptValue(db, record.payload) } satisfies EncryptedQueueRecord;
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    tx.objectStore(QUEUE_STORE).put(storedRecord);
    await transactionComplete(tx);
    emitOfflineChange();
    return record;
  },

  async updateOperation(id, patch) {
    const db = await openOfflineDb();
    const readTx = db.transaction(QUEUE_STORE, 'readonly');
    const current = await requestToPromise<EncryptedQueueRecord | undefined>(
      readTx.objectStore(QUEUE_STORE).get(id)
    );
    if (current) {
      const decrypted = await decryptQueueRecord(db, current);
      const next = { ...decrypted, ...patch, updatedAt: nowIso() };
      const storedRecord = { ...next, payload: await encryptValue(db, next.payload) } satisfies EncryptedQueueRecord;
      const writeTx = db.transaction(QUEUE_STORE, 'readwrite');
      writeTx.objectStore(QUEUE_STORE).put(storedRecord);
      await transactionComplete(writeTx);
    }
    emitOfflineChange();
  },

  async removeOperation(id) {
    const db = await openOfflineDb();
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    tx.objectStore(QUEUE_STORE).delete(id);
    await transactionComplete(tx);
    emitOfflineChange();
  },
};
